import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  Button,
  LinearProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
// import HomeIcon from '@mui/icons-material/Home';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ErrorIcon from '@mui/icons-material/Error';
import StarIcon from '@mui/icons-material/Star';
import BusinessIcon from '@mui/icons-material/Business';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { auth } from '../firebase';

const API_URL = 'https://turismo.nara.chat/api';

// Función para tiempo relativo
const tiempoRelativo = (fecha) => {
  if (!fecha) return 'N/A';
  const ahora = new Date();
  const fechaObj = new Date(fecha);
  const diff = Math.floor((ahora - fechaObj) / 1000);

  if (diff < 60) return 'Ahora';
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `Hace ${Math.floor(diff / 86400)} días`;
  return fechaObj.toLocaleDateString('es-MX');
};

// Patrón visual unificado para todos los paneles del Resumen.
// glassmorphism + barra de gradiente arriba (mismo patrón que Dashboard/Booking/KpiBar).
const glassPanel = (gradient) => ({
  position: 'relative',
  p: 3,
  borderRadius: 4,
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 3,
    background: gradient || 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  },
});

const Analytics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('7d');
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState('');
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(localStorage.getItem('user_id'));

  // Tab state - read from URL query param (reactivo a cambios de URL)
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get('tab') === 'whatsapp' ? 1 : 0;
  const [currentTab, setCurrentTab] = useState(tabFromUrl);

  // Sincronizar currentTab con el query param cuando cambia el URL
  // (necesario para AnalyticsLayout que navega vía ?tab=).
  useEffect(() => {
    setCurrentTab(tabFromUrl);
  }, [tabFromUrl]);

  // WhatsApp states
  const [sesiones, setSesiones] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loadingWhatsApp, setLoadingWhatsApp] = useState(false);
  const [busquedaWhatsApp, setBusquedaWhatsApp] = useState('');

  // Dialog enviar mensaje
  const [dialogEnviar, setDialogEnviar] = useState(false);
  const [numeroDestino, setNumeroDestino] = useState('');
  const [mensajeEnviar, setMensajeEnviar] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState(null);
  const [successEnvio, setSuccessEnvio] = useState(null);

  // Check auth and get user_id if needed
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      if (!userId && user.uid) {
        try {
          const response = await fetch(`${API_URL}/users/by_uid/${user.uid}`);
          if (response.ok) {
            const data = await response.json();
            if (data.id) {
              localStorage.setItem('user_id', data.id);
              setUserId(data.id);
            }
          }
        } catch (err) {
          console.error('Error obteniendo user_id:', err);
        }
      }
    });
    return () => unsubscribe();
  }, [navigate, userId]);

  // Fetch analytics data
  useEffect(() => {
    if (userId) {
      fetchAnalytics();
    }
  }, [periodo, empresaSeleccionada, userId]);

  const fetchAnalytics = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ periodo });
      if (empresaSeleccionada) {
        params.append('empresa_id', empresaSeleccionada);
      }

      const response = await fetch(
        `${API_URL}/analytics/dashboard?${params}`,
        {
          headers: {
            'X-User-Id': userId,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // Mapear campos del backend (citas) a frontend (bookings)
      if (data.resumen) {
        data.resumen.total_bookings = data.resumen.total_citas ?? data.resumen.total_bookings ?? 0;
        data.resumen.bookings_confirmados = data.resumen.citas_confirmadas ?? data.resumen.bookings_confirmados ?? 0;
        data.resumen.bookings_cancelados = data.resumen.citas_canceladas ?? data.resumen.bookings_cancelados ?? 0;
        data.resumen.bookings_pendientes = data.resumen.citas_pendientes ?? data.resumen.bookings_pendientes ?? 0;
        data.resumen.clientes_con_booking = data.resumen.clientes_con_cita ?? data.resumen.clientes_con_booking ?? 0;
      }
      if (data.comparacion) {
        data.comparacion.cambio_bookings = data.comparacion.cambio_citas ?? data.comparacion.cambio_bookings ?? 0;
      }
      if (data.citas_detalle) {
        data.bookings_detalle = data.citas_detalle;
      }
      if (data.graficos?.citas_por_dia) {
        data.graficos.bookings_por_dia = data.graficos.citas_por_dia;
      }
      setDatos(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const periodos = [
    { value: '7d', label: '7 días' },
    { value: '30d', label: '30 días' },
    { value: '90d', label: '90 días' }
  ];

  // Cargar sesiones WhatsApp
  const cargarSesiones = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/whatsapp/sesiones`);
      if (res.ok) {
        const data = await res.json();
        const sesionesData = Array.isArray(data) ? data : (data.sesiones || []);
        setSesiones(sesionesData);
      } else {
        setSesiones([]);
      }
    } catch (err) {
      console.error('Error cargando sesiones:', err);
      setSesiones([]);
    }
  }, []);

  // Cargar clientes WhatsApp
  const cargarClientes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/clientes-whatsapp/`);
      if (res.ok) {
        const data = await res.json();
        setClientes(Array.isArray(data) ? data : (data.clientes || []));
      } else {
        setClientes([]);
      }
    } catch (err) {
      console.error('Error cargando clientes:', err);
      setClientes([]);
    }
  }, []);

  // Cargar datos WhatsApp cuando se cambia a ese tab
  useEffect(() => {
    if (currentTab === 1 && sesiones.length === 0 && clientes.length === 0) {
      setLoadingWhatsApp(true);
      Promise.all([cargarSesiones(), cargarClientes()])
        .finally(() => setLoadingWhatsApp(false));
    }
  }, [currentTab, sesiones.length, clientes.length, cargarSesiones, cargarClientes]);

  // Refrescar datos WhatsApp
  const handleRefreshWhatsApp = async () => {
    setLoadingWhatsApp(true);
    await Promise.all([cargarSesiones(), cargarClientes()]);
    setLoadingWhatsApp(false);
  };

  // Enviar mensaje WhatsApp
  const handleEnviarMensaje = async () => {
    if (!numeroDestino || !mensajeEnviar) return;

    setEnviando(true);
    setErrorEnvio(null);
    try {
      const res = await fetch(`${API_URL}/whatsapp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: numeroDestino.startsWith('whatsapp:') ? numeroDestino : `whatsapp:${numeroDestino}`,
          body: mensajeEnviar
        })
      });

      if (res.ok) {
        setSuccessEnvio('Mensaje enviado correctamente');
        setDialogEnviar(false);
        setNumeroDestino('');
        setMensajeEnviar('');
        setTimeout(() => setSuccessEnvio(null), 3000);
        cargarSesiones();
      } else {
        const errorData = await res.json();
        setErrorEnvio(errorData.detail || 'Error al enviar mensaje');
      }
    } catch (err) {
      setErrorEnvio('Error de conexión al enviar mensaje');
    } finally {
      setEnviando(false);
    }
  };

  // Filtrar sesiones y clientes
  const sesionesFiltradas = (sesiones || []).filter(s =>
    s?.numero_telefono?.toLowerCase().includes(busquedaWhatsApp.toLowerCase()) ||
    s?.nodo_actual?.toLowerCase().includes(busquedaWhatsApp.toLowerCase())
  );

  const clientesFiltrados = (clientes || []).filter(c =>
    c?.nombre?.toLowerCase().includes(busquedaWhatsApp.toLowerCase()) ||
    c?.numero_whatsapp?.toLowerCase().includes(busquedaWhatsApp.toLowerCase()) ||
    c?.correo?.toLowerCase().includes(busquedaWhatsApp.toLowerCase())
  );

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    // Update URL without navigation
    const newUrl = newValue === 1 ? '/analytics?tab=whatsapp' : '/analytics';
    window.history.replaceState(null, '', newUrl);
  };

  // KPI Card Component
  const KPICard = ({ titulo, valor, subtitulo, cambio, icono, gradiente, shadowColor }) => {
    const esCrecimiento = cambio >= 0;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '0 16px 16px 0',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,253,250,0.85) 100%)',
          backdropFilter: 'blur(20px)',
          borderLeft: `4px solid ${shadowColor ? shadowColor.replace(/[\d.]+\)$/, '0.8)') : '#0891b2'}`,
          borderTop: '1px solid rgba(8, 145, 178, 0.08)',
          borderRight: '1px solid rgba(8, 145, 178, 0.08)',
          borderBottom: '1px solid rgba(8, 145, 178, 0.08)',
          boxShadow: '0 4px 24px rgba(8, 145, 178, 0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 32px ${shadowColor || 'rgba(8,145,178,0.12)'}`,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0, right: 0, width: 80, height: 40,
            background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 40'%3E%3Cpath d='M80 0 C65 0 60 12 45 12 C30 12 28 0 15 0 C8 0 3 4 0 8 L0 0 Z' fill='%230891b2' opacity='0.04'/%3E%3Cpath d='M80 0 C70 0 65 8 55 8 C45 8 40 0 30 0 C20 0 15 6 0 6 L0 0 Z' fill='%2306b6d4' opacity='0.03'/%3E%3C/svg%3E") no-repeat`,
            backgroundSize: 'cover',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              background: gradiente,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 8px 20px ${shadowColor}`,
              '& svg': { color: '#fff', fontSize: 24 }
            }}
          >
            {icono}
          </Box>
          {cambio !== null && (
            <Chip
              size="small"
              icon={esCrecimiento ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
              label={`${esCrecimiento ? '+' : ''}${cambio}%`}
              sx={{
                bgcolor: esCrecimiento ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                color: esCrecimiento ? '#059669' : '#dc2626',
                fontWeight: 700,
                fontSize: 12,
                '& .MuiChip-icon': { color: 'inherit' }
              }}
            />
          )}
        </Box>
        <Typography
          sx={{
            fontSize: 36,
            fontWeight: 800,
            background: gradiente,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            lineHeight: 1.1,
            mb: 0.5
          }}
        >
          {valor}
        </Typography>
        <Typography sx={{ color: '#1e293b', fontWeight: 600, fontSize: 14, mb: 0.25 }}>
          {titulo}
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: 12 }}>
          {subtitulo}
        </Typography>
      </Paper>
    );
  };

  // Insight Card Component
  const InsightCard = ({ insight }) => {
    const iconMap = {
      trending_up: <TrendingUpIcon />,
      trending_down: <TrendingDownIcon />,
      lightbulb: <LightbulbIcon />,
      warning: <WarningAmberIcon />,
      star: <StarIcon />,
      calendar_today: <CalendarTodayIcon />,
      error: <ErrorIcon />
    };

    const colorMap = {
      exito: { bg: 'rgba(16, 185, 129, 0.1)', border: '#10b981', icon: '#059669' },
      alerta: { bg: 'rgba(245, 158, 11, 0.1)', border: '#f59e0b', icon: '#d97706' },
      oportunidad: { bg: 'rgba(99, 102, 241, 0.1)', border: '#6366f1', icon: '#4f46e5' },
      info: { bg: 'rgba(14, 165, 233, 0.1)', border: '#0ea5e9', icon: '#0284c7' }
    };

    const colors = colorMap[insight.tipo] || colorMap.info;

    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: colors.bg,
          borderLeft: `4px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2
        }}
      >
        <Box sx={{ color: colors.icon, mt: 0.25 }}>
          {iconMap[insight.icono] || <LightbulbIcon />}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 14, mb: 0.5 }}>
            {insight.titulo}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            {insight.descripcion}
          </Typography>
        </Box>
        {insight.accion && (
          <Button
            size="small"
            onClick={() => navigate(insight.accion.ruta)}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            sx={{
              color: colors.icon,
              fontWeight: 600,
              fontSize: 12,
              textTransform: 'none',
              whiteSpace: 'nowrap'
            }}
          >
            {insight.accion.texto}
          </Button>
        )}
      </Box>
    );
  };

  // Chatbot Card Component
  const ChatbotCard = ({ chatbot, isTop }) => {
    const maxConversion = 100;
    const progress = Math.min(chatbot.tasa_conversion, maxConversion);

    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: '#fff',
          border: isTop ? '2px solid #10b981' : '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          position: 'relative',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
          }
        }}
      >
        {isTop && (
          <Chip
            icon={<StarIcon sx={{ fontSize: 12 }} />}
            label="Mejor"
            size="small"
            sx={{
              position: 'absolute',
              top: -10,
              right: 12,
              bgcolor: '#10b981',
              color: '#fff',
              fontWeight: 600,
              fontSize: 10,
              '& .MuiChip-icon': { color: '#fff' }
            }}
          />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              fontSize: 16,
              fontWeight: 600
            }}
          >
            {chatbot.nombre.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {chatbot.nombre}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 11 }}>
              {chatbot.propietario}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(99, 102, 241, 0.05)', borderRadius: 2 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#6366f1' }}>
              {chatbot.bookings}
            </Typography>
            <Typography sx={{ fontSize: 10, color: '#64748b' }}>Bookings</Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(16, 185, 129, 0.05)', borderRadius: 2 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#10b981' }}>
              {chatbot.clientes}
            </Typography>
            <Typography sx={{ fontSize: 10, color: '#64748b' }}>Clientes</Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(245, 158, 11, 0.05)', borderRadius: 2 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>
              {chatbot.conversaciones}
            </Typography>
            <Typography sx={{ fontSize: 10, color: '#64748b' }}>Chats</Typography>
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>Conversión</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#1e293b' }}>
              {chatbot.tasa_conversion}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'rgba(0, 0, 0, 0.06)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: chatbot.tasa_conversion >= 30
                  ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                  : chatbot.tasa_conversion >= 15
                    ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                    : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
              }
            }}
          />
        </Box>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        py: 3,
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: { xs: 20, sm: 24 } }}>
            Analíticas
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            Rendimiento de tus chatbots
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Company selector (superadmin only) */}
          {datos?.es_superadmin && datos?.empresas?.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>
                Empresa
              </InputLabel>
              <Select
                value={empresaSeleccionada}
                label="Empresa"
                onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                startAdornment={<BusinessIcon sx={{ color: '#64748b', mr: 1, fontSize: 18 }} />}
              >
                <MenuItem value="">Todas las empresas</MenuItem>
                {datos.empresas.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.nombre} ({emp.chatbots})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Period selector */}
          <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#e2e8f0', borderRadius: 2, p: 0.5 }}>
            {periodos.map((p) => (
              <Button
                key={p.value}
                size="small"
                onClick={() => setPeriodo(p.value)}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  color: periodo === p.value ? '#1e293b' : '#64748b',
                  bgcolor: periodo === p.value ? '#fff' : 'transparent',
                  fontWeight: periodo === p.value ? 600 : 400,
                  fontSize: 13,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: periodo === p.value ? '#fff' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                {p.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Tabs — ocultas: la navegación ahora viene de AnalyticsLayout (sidebar/tabs móviles) */}
      <Box sx={{ display: 'none', mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTabs-indicator': {
              bgcolor: '#164e63',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab
            icon={<AssessmentIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Resumen"
            sx={{
              color: '#64748b',
              '&.Mui-selected': { color: '#164e63' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
              minHeight: 48
            }}
          />
          <Tab
            icon={<WhatsAppIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="WhatsApp"
            sx={{
              color: '#64748b',
              '&.Mui-selected': { color: '#164e63' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
              minHeight: 48
            }}
          />
        </Tabs>
      </Box>

      {/* Success message for WhatsApp */}
      {successEnvio && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessEnvio(null)}>
          {successEnvio}
        </Alert>
      )}

      {/* Tab Panel: Resumen */}
      {currentTab === 0 && (
        <>
          {/* Loading state */}
          {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#6366f1' }} />
        </Box>
      )}

      {/* Error state */}
      {error && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', background: '#fff', borderRadius: 3 }}>
          <Typography sx={{ color: '#ef4444', fontWeight: 600, mb: 1 }}>
            Error al cargar las analíticas
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 14 }}>
            {error}
          </Typography>
        </Paper>
      )}

      {/* Data loaded */}
      {!loading && datos && (
        <>
          {/* Superadmin badge */}
          {datos.es_superadmin && (
            <Box
              sx={{
                mb: 3,
                p: 1.5,
                background: 'rgba(139, 92, 246, 0.08)',
                borderRadius: 2,
                border: '1px solid rgba(139, 92, 246, 0.2)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <BusinessIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />
              <Typography sx={{ color: '#6d28d9', fontSize: 13, fontWeight: 500 }}>
                {datos.empresa_actual
                  ? `Mostrando: ${datos.empresas?.find(e => e.id === datos.empresa_actual)?.nombre || 'Empresa'}`
                  : 'Vista global: Todas las empresas'
                }
              </Typography>
            </Box>
          )}

          {/* KPI Cards - Executive Summary */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 3
            }}
          >
            <KPICard
              titulo="Bookings"
              valor={datos.resumen.total_bookings}
              subtitulo={`${datos.resumen.bookings_confirmados} confirmadas`}
              cambio={datos.comparacion.cambio_bookings}
              icono={<EventAvailableIcon />}
              gradiente="linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
              shadowColor="rgba(99, 102, 241, 0.3)"
            />
            <KPICard
              titulo="Clientes Nuevos"
              valor={datos.resumen.clientes_nuevos}
              subtitulo={`${datos.resumen.clientes_con_booking} reservaron`}
              cambio={datos.comparacion.cambio_clientes}
              icono={<PersonAddIcon />}
              gradiente="linear-gradient(135deg, #10b981 0%, #059669 100%)"
              shadowColor="rgba(16, 185, 129, 0.3)"
            />
            <KPICard
              titulo="Tasa Conversión"
              valor={`${datos.resumen.tasa_conversion}%`}
              subtitulo="Clientes → Booking"
              cambio={null}
              icono={<SyncAltIcon />}
              gradiente="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
              shadowColor="rgba(245, 158, 11, 0.3)"
            />
            <KPICard
              titulo="Tiempo Ahorrado"
              valor={`${datos.resumen.horas_ahorradas}h`}
              subtitulo={`${datos.resumen.total_conversaciones} chats atendidos`}
              cambio={datos.comparacion.cambio_conversaciones}
              icono={<AccessTimeIcon />}
              gradiente="linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
              shadowColor="rgba(14, 165, 233, 0.3)"
            />
          </Box>

          {/* Insights Section */}
          {datos.insights && datos.insights.length > 0 && (
            <Paper
              elevation={0}
              sx={{ ...glassPanel('linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'), mb: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LightbulbIcon sx={{ color: '#f59e0b' }} />
                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>
                  Insights y Recomendaciones
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {datos.insights.map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </Box>
            </Paper>
          )}

          {/* Main content grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
            {/* Left column */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Chatbots Performance */}
              {datos.chatbots && datos.chatbots.length > 0 && (
                <Paper
                  elevation={0}
                  sx={glassPanel('linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SmartToyIcon sx={{ color: '#6366f1' }} />
                      <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>
                        Rendimiento por Chatbot
                      </Typography>
                    </Box>
                    <Chip
                      label={`${datos.chatbots.length} chatbots`}
                      size="small"
                      sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1', fontWeight: 600 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                      gap: 2
                    }}
                  >
                    {datos.chatbots.map((chatbot, idx) => (
                      <ChatbotCard key={chatbot.id} chatbot={chatbot} isTop={idx === 0 && datos.chatbots.length > 1} />
                    ))}
                  </Box>
                </Paper>
              )}

              {/* Charts Grid 2x2 */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                {/* Chart 1: Conversaciones por Día (stacked) */}
                {datos.graficos?.conversaciones_por_dia?.length > 0 && (
                  <Paper elevation={0} sx={glassPanel('linear-gradient(135deg, #10b981 0%, #059669 100%)')}>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 15, mb: 2 }}>
                      Conversaciones por Día
                    </Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={datos.graficos.conversaciones_por_dia}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="fecha" stroke="#94a3b8" fontSize={11}
                          tickFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }} />
                        <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                        <ChartTooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}
                          labelFormatter={(v) => new Date(v).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="completadas" stackId="a" fill="#10b981" name="Completadas" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="escaladas" stackId="a" fill="#f59e0b" name="Escaladas" />
                        <Bar dataKey="timeout" stackId="a" fill="#ef4444" name="Timeout" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                )}

                {/* Chart 2: Bookings por Día (stacked) */}
                {datos.graficos?.bookings_por_dia?.length > 0 && (
                  <Paper elevation={0} sx={glassPanel('linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)')}>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 15, mb: 2 }}>
                      Bookings por Día
                    </Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={datos.graficos.bookings_por_dia}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="fecha" stroke="#94a3b8" fontSize={11}
                          tickFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }} />
                        <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                        <ChartTooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}
                          labelFormatter={(v) => new Date(v).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="confirmadas" stackId="a" fill="#10b981" name="Confirmadas" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="pendientes" stackId="a" fill="#f59e0b" name="Pendientes" />
                        <Bar dataKey="canceladas" stackId="a" fill="#ef4444" name="Canceladas" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                )}

                {/* Chart 3: Actividad por Hora */}
                {datos.graficos?.distribucion_horaria && (
                  <Paper elevation={0} sx={glassPanel('linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)')}>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 15, mb: 2 }}>
                      Actividad por Hora del Día
                    </Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={datos.graficos.distribucion_horaria}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="hora" stroke="#94a3b8" fontSize={11}
                          tickFormatter={(v) => `${v}h`} />
                        <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                        <ChartTooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}
                          labelFormatter={(v) => `${v}:00 - ${v}:59`} />
                        <Bar dataKey="conversaciones" fill="#6366f1" radius={[4, 4, 0, 0]} name="Conversaciones" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                )}

                {/* Chart 4: Temas Frecuentes (horizontal) */}
                {datos.graficos?.temas_frecuentes?.length > 0 && (
                  <Paper elevation={0} sx={glassPanel('linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)')}>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 15, mb: 2 }}>
                      Temas Más Frecuentes
                    </Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={datos.graficos.temas_frecuentes} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                        <YAxis type="category" dataKey="tema" stroke="#94a3b8" fontSize={11} width={100}
                          tickFormatter={(v) => v.length > 14 ? v.slice(0, 14) + '...' : v} />
                        <ChartTooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                        <Bar dataKey="cantidad" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Conversaciones" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                )}
              </Box>
            </Box>

            {/* Right column */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Upcoming Appointments */}
              <Paper
                elevation={0}
                sx={glassPanel('linear-gradient(135deg, #f59e0b 0%, #d97706 100%)')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarTodayIcon sx={{ color: '#f59e0b' }} />
                  <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>
                    Próximos Bookings
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 3,
                      bgcolor: datos.bookings_detalle?.bookings_hoy > 0 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                      textAlign: 'center'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 32,
                        fontWeight: 800,
                        color: datos.bookings_detalle?.bookings_hoy > 0 ? '#f59e0b' : '#94a3b8'
                      }}
                    >
                      {datos.bookings_detalle?.bookings_hoy || 0}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                      Hoy
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 3,
                      bgcolor: datos.bookings_detalle?.bookings_manana > 0 ? 'rgba(99, 102, 241, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                      textAlign: 'center'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 32,
                        fontWeight: 800,
                        color: datos.bookings_detalle?.bookings_manana > 0 ? '#6366f1' : '#94a3b8'
                      }}
                    >
                      {datos.bookings_detalle?.bookings_manana || 0}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                      Mañana
                    </Typography>
                  </Box>
                </Box>
                <Button
                  fullWidth
                  onClick={() => navigate('/agenda')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    mt: 2,
                    py: 1,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                    }
                  }}
                >
                  Ver Agenda Completa
                </Button>
              </Paper>

              {/* Quick Stats */}
              <Paper
                elevation={0}
                sx={glassPanel('linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)')}
              >
                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16, mb: 2 }}>
                  Resumen del Período
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#64748b', fontSize: 13 }}>Bookings confirmados</Typography>
                    <Typography sx={{ fontWeight: 600, color: '#10b981', fontSize: 14 }}>
                      {datos.resumen.bookings_confirmados}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#64748b', fontSize: 13 }}>Bookings cancelados</Typography>
                    <Typography sx={{ fontWeight: 600, color: '#ef4444', fontSize: 14 }}>
                      {datos.resumen.bookings_cancelados}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#64748b', fontSize: 13 }}>Bookings pendientes</Typography>
                    <Typography sx={{ fontWeight: 600, color: '#f59e0b', fontSize: 14 }}>
                      {datos.resumen.bookings_pendientes}
                    </Typography>
                  </Box>
                  <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 1.5, mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#64748b', fontSize: 13 }}>Tasa confirmación</Typography>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>
                      {datos.resumen.tasa_confirmacion}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Empty state for no chatbots */}
              {(!datos.chatbots || datos.chatbots.length === 0) && (
                <Paper
                  elevation={0}
                  sx={{ ...glassPanel('linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'), textAlign: 'center', p: 4 }}
                >
                  <SmartToyIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                    Sin chatbots
                  </Typography>
                  <Typography sx={{ color: '#64748b', fontSize: 13, mb: 2 }}>
                    No tienes chatbots configurados aún
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/admin-planes')}
                    sx={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Configurar Chatbot
                  </Button>
                </Paper>
              )}
            </Box>
          </Box>
        </>
      )}
        </>
      )}

      {/* Tab Panel: WhatsApp */}
      {currentTab === 1 && (
        <>
          {/* Loading WhatsApp */}
          {loadingWhatsApp && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#25D366' }} />
            </Box>
          )}

          {!loadingWhatsApp && (
            <>
              {/* Header con búsqueda y acciones */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 3,
                  bgcolor: '#fff',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2
                }}
              >
                <TextField
                  size="small"
                  placeholder="Buscar por número, nombre o correo..."
                  value={busquedaWhatsApp}
                  onChange={(e) => setBusquedaWhatsApp(e.target.value)}
                  sx={{ width: { xs: '100%', sm: 300 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Actualizar">
                    <IconButton onClick={handleRefreshWhatsApp} sx={{ color: '#25D366' }}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={() => setDialogEnviar(true)}
                    sx={{
                      bgcolor: '#25D366',
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#1ebe5d' }
                    }}
                  >
                    Enviar Mensaje
                  </Button>
                </Box>
              </Paper>

              {/* Stats Cards */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                {[
                  { label: 'Sesiones Activas', value: sesiones.length, color: '#25D366', icon: <ChatIcon /> },
                  { label: 'Clientes Registrados', value: clientes.length, color: '#128C7E', icon: <PersonIcon /> },
                  { label: 'Modo Agente', value: sesiones.filter(s => s.agent_mode).length, color: '#f59e0b', icon: <SupportAgentIcon /> },
                  { label: 'Nuevos Hoy', value: clientes.filter(c => {
                    if (!c.created_at) return false;
                    const hoy = new Date().toDateString();
                    return new Date(c.created_at).toDateString() === hoy;
                  }).length, color: '#6366f1', icon: <PersonIcon /> }
                ].map((stat) => (
                  <Paper
                    key={stat.label}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: '#fff',
                      border: '1px solid #e2e8f0',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: stat.color
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: `${stat.color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '& svg': { color: stat.color, fontSize: 22 }
                        }}
                      >
                        {stat.icon}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 24, fontWeight: 800, color: stat.color, lineHeight: 1 }}>
                          {stat.value}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                          {stat.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>

              {/* Main Grid: Sesiones y Clientes */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
                {/* Sesiones/Conversaciones */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ p: 2, bgcolor: 'rgba(37, 211, 102, 0.1)', borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChatIcon sx={{ color: '#25D366' }} />
                      <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 15 }}>
                        Conversaciones Recientes
                      </Typography>
                      <Chip label={sesionesFiltradas.length} size="small" sx={{ bgcolor: '#25D366', color: '#fff', fontWeight: 600, ml: 'auto' }} />
                    </Box>
                  </Box>

                  {sesionesFiltradas.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <ChatIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
                      <Typography sx={{ color: '#64748b', fontWeight: 500 }}>No hay conversaciones activas</Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>Las conversaciones aparecerán cuando los usuarios interactúen</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                      {sesionesFiltradas.slice(0, 20).map((sesion, idx) => (
                        <Box
                          key={sesion.id}
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            borderBottom: idx < sesionesFiltradas.length - 1 ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' }
                          }}
                        >
                          <Avatar sx={{ bgcolor: '#25D366', width: 40, height: 40 }}>
                            <WhatsAppIcon sx={{ fontSize: 22 }} />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {sesion.numero_telefono}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                              Nodo: {sesion.nodo_actual || 'inicio'}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                              {tiempoRelativo(sesion.updated_at)}
                            </Typography>
                            {sesion.agent_mode && (
                              <Chip
                                size="small"
                                label="Agente"
                                sx={{ mt: 0.5, bgcolor: '#fef3c7', color: '#d97706', fontWeight: 600, fontSize: 9, height: 18 }}
                              />
                            )}
                          </Box>
                          <Tooltip title="Enviar mensaje">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setNumeroDestino(sesion.numero_telefono);
                                setDialogEnviar(true);
                              }}
                              sx={{ color: '#25D366' }}
                            >
                              <SendIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>

                {/* Clientes */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ p: 2, bgcolor: 'rgba(18, 140, 126, 0.1)', borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ color: '#128C7E' }} />
                      <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 15 }}>
                        Clientes WhatsApp
                      </Typography>
                      <Chip label={clientesFiltrados.length} size="small" sx={{ bgcolor: '#128C7E', color: '#fff', fontWeight: 600, ml: 'auto' }} />
                    </Box>
                  </Box>

                  {clientesFiltrados.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <PersonIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
                      <Typography sx={{ color: '#64748b', fontWeight: 500 }}>No hay clientes registrados</Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>Los clientes se registran durante las conversaciones</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                      {clientesFiltrados.slice(0, 20).map((cliente, idx) => (
                        <Box
                          key={cliente.id}
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            borderBottom: idx < clientesFiltrados.length - 1 ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' }
                          }}
                        >
                          <Avatar sx={{ bgcolor: '#128C7E', width: 40, height: 40, fontSize: 14, fontWeight: 600 }}>
                            {cliente.nombre?.charAt(0)?.toUpperCase() || <PersonIcon />}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {cliente.nombre || 'Sin nombre'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 12, color: '#25D366' }} />
                                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{cliente.numero_whatsapp}</Typography>
                              </Box>
                              {cliente.correo && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <EmailIcon sx={{ fontSize: 12, color: '#6366f1' }} />
                                  <Typography sx={{ fontSize: 11, color: '#64748b', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cliente.correo}</Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                          <Tooltip title="Enviar mensaje">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setNumeroDestino(cliente.numero_whatsapp);
                                setDialogEnviar(true);
                              }}
                              sx={{ color: '#25D366' }}
                            >
                              <SendIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Box>
            </>
          )}
        </>
      )}

      {/* Dialog: Enviar mensaje WhatsApp */}
      <Dialog
        open={dialogEnviar}
        onClose={() => setDialogEnviar(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          <Typography sx={{ fontWeight: 600 }}>Enviar Mensaje de WhatsApp</Typography>
        </DialogTitle>
        <DialogContent>
          {errorEnvio && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }} onClose={() => setErrorEnvio(null)}>
              {errorEnvio}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Número de destino"
            placeholder="+521234567890"
            value={numeroDestino}
            onChange={(e) => setNumeroDestino(e.target.value)}
            sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#128C7E' }} /></InputAdornment>
            }}
            helperText="Incluye el código de país (ej: +52 para México)"
          />

          <TextField
            fullWidth
            label="Mensaje"
            multiline
            rows={4}
            value={mensajeEnviar}
            onChange={(e) => setMensajeEnviar(e.target.value)}
            placeholder="Escribe tu mensaje aquí..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogEnviar(false)} color="inherit" sx={{ borderRadius: 2 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleEnviarMensaje}
            disabled={enviando || !numeroDestino || !mensajeEnviar}
            startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            sx={{ bgcolor: '#25D366', borderRadius: 2, '&:hover': { bgcolor: '#1ebe5d' } }}
          >
            {enviando ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Analytics;
